/**
 * Pinned gadgets (#2075). Server-backed: D1 `user_pins` via
 * GET /api/artifacts/pins, PUT/DELETE /api/artifacts/:id/pin (owner-scoped).
 * The hook shape is the one the mockup used, so the pin button and the Overview
 * row did not change. Toggles are optimistic and roll back on failure.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiMutate } from "@/lib/api";

export interface PinRow { artifact_id: string; position: number }
export interface PinsResponse { pins: PinRow[]; pending_migration?: boolean }

const KEY = ["gadget-pins"] as const;

export function usePinsQuery(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiFetch<PinsResponse>("/api/artifacts/pins"),
    staleTime: 60 * 1000,
    enabled: opts.enabled ?? true,
  });
}

/**
 * The pin control is unavailable until /pins has SUCCEEDED without
 * `pending_migration`: while loading, or after an error, a tap would 503 or
 * act on a list we do not have (brains-trust F7).
 */
export function pinsUnavailable(q: { isSuccess: boolean; data?: PinsResponse }): boolean {
  return !q.isSuccess || q.data?.pending_migration === true;
}

export function useGadgetPins(opts: { enabled?: boolean } = {}): {
  pins: string[];
  isPinned: (id: string) => boolean;
  toggle: (id: string) => void;
  /** True until /pins succeeded, or while the host has not applied migrate-2075-user-pins.sql: hide the pin control. */
  unavailable: boolean;
} {
  const qc = useQueryClient();
  const q = usePinsQuery(opts);
  const pins = useMemo(() => (q.data?.pins ?? []).map((p) => p.artifact_id), [q.data]);

  const mutation = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      apiMutate<{ ok: boolean; pinned: boolean }>(`/api/artifacts/${id}/pin`, pinned ? "DELETE" : "PUT"),
    onMutate: async ({ id, pinned }) => {
      await qc.cancelQueries({ queryKey: KEY });
      const prev = qc.getQueryData<PinsResponse>(KEY);
      qc.setQueryData<PinsResponse>(KEY, (cur) => {
        const list = cur?.pins ?? [];
        const next = pinned
          ? list.filter((p) => p.artifact_id !== id)
          : [...list, { artifact_id: id, position: (list[list.length - 1]?.position ?? 0) + 1 }];
        return { ...(cur ?? {}), pins: next };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => { if (ctx?.prev) qc.setQueryData(KEY, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const { mutate } = mutation;
  const toggle = useCallback((id: string) => mutate({ id, pinned: pins.includes(id) }), [mutate, pins]);

  return {
    pins,
    isPinned: (id) => pins.includes(id),
    toggle,
    unavailable: pinsUnavailable(q),
  };
}
